import { useMutation, useQuery } from '@tanstack/react-query';
import { productsApi } from '@/lib/api-client';

export type ImportJobStatus = 'running' | 'succeeded' | 'failed';

export interface ImportJobSummary {
  id: string;
  status: ImportJobStatus;
  totalRows: number;
  uploadedRows: number;
  processedRows: number;
  result: {
    inserted: number;
    updated: number;
    /** Rows rejected for naming a department, category or vendor that doesn't
     *  exist. Optional because rows written before import stopped creating
     *  those don't carry it. */
    failed?: number;
    /** @deprecated Import matches these by name now and never creates them.
     *  Still typed so history rows recorded before that change still read. */
    departmentsCreated?: number;
    categoriesCreated?: number;
    vendorsCreated?: number;
  } | null;
  errors: string[];
  locationId: string | null;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
  startedAt: string | null;
  heartbeatAt: string | null;
  finishedAt: string | null;
}

/** Create a tracking row at the start of an import. The actual rows
 *  go through /products/bulk-import; this row is purely a history record. */
export function useCreateImportJob() {
  return useMutation({
    mutationFn: (data: { totalRows: number; locationId?: string | null; createdByName?: string | null }) =>
      productsApi.post<{ jobId: string; status: ImportJobStatus; totalRows: number }>('/products/import-jobs', data),
  });
}

/** Mark a tracking row succeeded/failed once the chunk loop completes. */
export function useFinishImportJob() {
  return useMutation({
    mutationFn: ({
      jobId,
      ...payload
    }: {
      jobId: string;
      status?: 'succeeded' | 'failed';
      processedRows?: number;
      result: {
        inserted: number;
        updated: number;
        failed?: number;
      };
      errors: string[];
    }) => productsApi.post<{ id: string; status: ImportJobStatus }>(`/products/import-jobs/${jobId}/finish`, payload),
  });
}

export function useImportJobs(params: { limit?: number; locationId?: string | null } = {}) {
  const limit = params.limit ?? 20;
  const locationId = params.locationId || undefined;
  return useQuery({
    queryKey: ['import-jobs', { limit, locationId }],
    queryFn: () =>
      productsApi.get<{ data: ImportJobSummary[] }>('/products/import-jobs', {
        limit,
        locationId,
      }),
  });
}
